import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../hooks/AuthContext';
import { InstallPrompt } from './InstallPrompt';
import { 
  Home, 
  BookOpen, 
  Users, 
  MessageSquare,
  Heart,
  Settings,
  LogOut,
  Menu,
  X,
  Shield,
  Bot,
  Trophy,
  FileText,
  Lock,
  Brain,
  HelpCircle
} from 'lucide-react';
import { TermsOfService } from '../legal/TermsOfService';
import { PrivacyPolicy } from '../legal/PrivacyPolicy';

export const UserLayout: React.FC = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profilePicture, setProfilePicture] = useState<string | null>(null);
  const [hasAccessCode, setHasAccessCode] = useState<boolean | null>(null);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/auth');
  };

  // Fetch user profile picture
  useEffect(() => {
    const fetchProfilePicture = async () => {
      if (!user) return;
      
      try {
        const profileDoc = await getDoc(doc(db, 'publicProfiles', user.uid));
        if (profileDoc.exists()) {
          const profileData = profileDoc.data();
          setProfilePicture(profileData.profilePicture || null);
        }
      } catch (error) {
        // Silent fail
      }
    };

    fetchProfilePicture();
  }, [user]);

  // Listen for profile picture updates
  useEffect(() => {
    const handleProfilePictureUpdate = (event: CustomEvent) => {
      const { profilePicture } = event.detail;
      setProfilePicture(profilePicture);
    };

    window.addEventListener('profilePictureUpdated', handleProfilePictureUpdate as EventListener);
    
    return () => {
      window.removeEventListener('profilePictureUpdated', handleProfilePictureUpdate as EventListener);
    };
  }, []);

  // Check if user has access code
  useEffect(() => {
    const checkAccessCode = async () => {
      if (!user) return;
      
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.accessCodeId) {
            // User has access code, verify it's still valid
            const accessCodeDoc = await getDoc(doc(db, 'accessCodes', userData.accessCodeId));
            if (accessCodeDoc.exists()) {
              const accessCodeData = accessCodeDoc.data();
              if (accessCodeData.isActive && 
                  (!accessCodeData.expiresAt || accessCodeData.expiresAt.toDate() > new Date())) {
                setHasAccessCode(true);
              } else {
                setHasAccessCode(false);
              }
            } else {
              setHasAccessCode(false);
            }
          } else {
            setHasAccessCode(false);
          }
        }
      } catch (error) {
        setHasAccessCode(false);
      }
    };

    checkAccessCode();
  }, [user]);

  // Build navigation items based on access code status
  const navItems = [
    { to: '/', label: 'Dashboard', icon: Home, exact: true },
    ...(hasAccessCode ? [{ to: '/training', label: 'Training', icon: BookOpen, premium: true }] : []),
    ...(hasAccessCode ? [{ to: '/meditations', label: 'Meditations', icon: Brain, premium: true }] : []),
    { to: '/journal', label: 'Journal', icon: MessageSquare },
    { to: '/grips', label: 'GRIPS', icon: Bot },
    { to: '/community', label: 'Community', icon: Users },
    ...(hasAccessCode ? [{ to: '/achievements', label: 'Achievements', icon: Trophy, premium: true }] : []),
    { to: '/profile/public', label: 'Profile', icon: Settings },
  ];

  return (
    <div className="bg-gray-50">
      {/* Mobile header */}
      <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 safe-top">
        <div className="flex items-center justify-between" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
          <Link 
            to="/" 
            className="flex items-center space-x-3 hover:opacity-80 transition-opacity"
            onClick={() => setMobileMenuOpen(false)}
          >
            <div className="w-8 h-8 flex items-center justify-center">
              <img src="/icons/GGGBadgeFINAL-01.svg" alt="GGG Logo" className="w-6 h-6 object-contain" />
            </div>
            <span className="text-lg font-semibold text-gray-900">
              Grit, Growth & Gratitude
            </span>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-gray-500 hover:text-gray-700"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-white border-b border-gray-200">
          <div className="px-4 py-2 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                onClick={() => setMobileMenuOpen(false)}
                className={({ isActive }) => `
                  flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors
                  ${isActive 
                    ? 'bg-red-50 text-red-700' 
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }
                `}
              >
                <item.icon className="w-5 h-5 mr-3" />
                <span className="flex-1">{item.label}</span>
                {item.premium && (
                  <Shield className="w-4 h-4 text-amber-500" title="Premium Feature" />
                )}
              </NavLink>
            ))}
            <div className="border-t border-gray-200 pt-2 mt-2">
              {/* Support Link */}
              <div className="mb-3">
                <NavLink
                  to="/support"
                  className={({ isActive }) =>
                    `flex items-center w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <HelpCircle className="w-4 h-4 mr-2" />
                  Tech Support
                </NavLink>
              </div>
              
              {/* Legal Links */}
              <div className="mb-3 space-y-1">
                <button
                  onClick={() => setShowTerms(true)}
                  className="flex items-center w-full px-3 py-2 text-xs text-gray-500 rounded-lg hover:bg-gray-50 hover:text-gray-700"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Terms of Service
                </button>
                <button
                  onClick={() => setShowPrivacy(true)}
                  className="flex items-center w-full px-3 py-2 text-xs text-gray-500 rounded-lg hover:bg-gray-50 hover:text-gray-700"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Privacy Policy
                </button>
              </div>
              
              {/* Sign Out Button */}
              <button
                onClick={handleSignOut}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-50 hover:text-gray-900"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex">
        {/* Desktop sidebar */}
        <div className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0">
          <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
            {/* Header */}
            <Link 
              to="/" 
              className="flex items-center px-6 py-4 border-b border-gray-200 hover:bg-gray-50 transition-colors cursor-pointer"
            >
              <div className="w-10 h-10 flex items-center justify-center mr-3">
                <img src="/icons/GGGBadgeFINAL-01.svg" alt="GGG Logo" className="w-8 h-8 object-contain" />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  Grit, Growth & Gratitude
                </h1>
                <p className="text-sm text-gray-500">First Responder Wellness</p>
              </div>
            </Link>

            {/* User info */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center">
                  {profilePicture ? (
                    <img
                      src={profilePicture}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                      <span className="text-gray-700 font-semibold">
                        {user?.displayName?.[0]?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {user?.displayName || 'User'}
                  </p>
                  <p className="text-sm text-gray-500 truncate">
                    {user?.responderType || 'First Responder'}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation */}
            <nav className="flex-1 px-6 py-4">
              <ul className="space-y-1">
                {navItems.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.exact}
                      className={({ isActive }) => `
                        flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors
                        ${isActive 
                          ? 'bg-red-50 text-red-700' 
                          : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        }
                      `}
                    >
                      <item.icon className="w-5 h-5 mr-3" />
                      <span className="flex-1">{item.label}</span>
                      {item.premium && (
                        <Shield className="w-4 h-4 text-amber-500" title="Premium Feature" />
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </nav>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-200">
              {/* Support Link */}
              <div className="mb-4">
                <NavLink
                  to="/support"
                  className={({ isActive }) =>
                    `flex items-center w-full px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`
                  }
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <HelpCircle className="w-4 h-4 mr-3" />
                  Tech Support
                </NavLink>
              </div>
              
              {/* Legal Links */}
              <div className="mb-4 space-y-2">
                <button
                  onClick={() => setShowTerms(true)}
                  className="flex items-center w-full px-3 py-2 text-sm text-gray-500 rounded-lg hover:bg-gray-50 hover:text-gray-700"
                >
                  <FileText className="w-4 h-4 mr-3" />
                  Terms of Service
                </button>
                <button
                  onClick={() => setShowPrivacy(true)}
                  className="flex items-center w-full px-3 py-2 text-sm text-gray-500 rounded-lg hover:bg-gray-50 hover:text-gray-700"
                >
                  <Lock className="w-4 h-4 mr-3" />
                  Privacy Policy
                </button>
              </div>
              
              {/* Sign Out Button */}
              <button
                onClick={handleSignOut}
                className="flex items-center w-full px-3 py-2 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-50 hover:text-gray-900"
              >
                <LogOut className="w-5 h-5 mr-3" />
                Sign Out
              </button>
            </div>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 lg:ml-64">
          <main className="p-4 md:p-6">
            <Outlet />
          </main>
        </div>
      </div>

      {/* PWA Install Prompt */}
      <InstallPrompt />
      
      {/* Legal Document Modals */}
      {showTerms && (
        <TermsOfService onClose={() => setShowTerms(false)} />
      )}
      
      {showPrivacy && (
        <PrivacyPolicy onClose={() => setShowPrivacy(false)} />
      )}
    </div>
  );
};
